import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/private/services/api.service';
import { environment } from 'src/environments/environment';

export type WorkflowStatus = 'draft' | 'active' | 'paused' | 'archived';

export interface WorkflowSummary {
  id: number;
  client_id: number;
  name: string;
  description: string | null;
  status: WorkflowStatus;
  version: number;
  trigger_type: string | null;
  /** Number of steps on the canvas (the list API does not return the graph). */
  node_count: number | string;
  last_published_at: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowListResponse {
  status: boolean;
  data: WorkflowSummary[];
  count: number;
}

/** Workflow Automation APIs (part-g). */
@Injectable({ providedIn: 'root' })
export class WorkflowService {
  constructor(private api: ApiService) {}

  list(data: {
    client_id: number | string;
    page?: number;
    limit?: number;
    search?: string;
    status?: WorkflowStatus;
    module?: 'campaign' | 'lead' | 'deal' | 'contact';
  }): Observable<WorkflowListResponse> {
    return this.api.post(environment.getAllWorkflows, data);
  }

  get(data: { client_id: number | string; workflow_id: number }): Observable<any> {
    return this.api.post(environment.getWorkflow, data);
  }

  runs(data: { client_id: number | string; workflow_id: number; limit?: number }): Observable<any> {
    return this.api.post(environment.getWorkflowRuns, data);
  }

  create(data: {
    client_id: number | string;
    name: string;
    description?: string;
    created_by?: number | string;
    settings?: { module?: 'campaign' | 'lead' | 'deal' | 'contact' };
  }): Observable<any> {
    return this.api.post(environment.createWorkflow, data);
  }

  update(data: any): Observable<any> {
    return this.api.post(environment.updateWorkflow, data);
  }

  setStatus(data: {
    client_id: number | string;
    workflow_id: number;
    status: 'active' | 'paused';
    updated_by?: number | string;
  }): Observable<any> {
    return this.api.post(environment.updateWorkflowStatus, data);
  }

  /** Archives the workflow (soft delete). */
  delete(data: {
    client_id: number | string;
    workflow_id: number;
    updated_by?: number | string;
  }): Observable<any> {
    return this.api.post(environment.deleteWorkflow, data);
  }
}
